export function buildPrintHead() {
  const linkTags = Array.from(
    document.querySelectorAll('link[rel="stylesheet"]')
  )
    .map(link => `<link rel="stylesheet" href="${link.href}">`)
    .join('\n');
  const styleTags = Array.from(document.querySelectorAll('style'))
    .map(style => style.outerHTML)
    .join('\n');
  let cssText = '';
  Array.from(document.styleSheets).forEach(sheet => {
    try {
      // Check if we can access the stylesheet
      if (!sheet.href || sheet.href.startsWith(window.location.origin)) {
        const rules = sheet.cssRules || sheet.rules || [];
        cssText += Array.from(rules)
          .map(rule => rule.cssText)
          .join('\n');
      }
    } catch (e) {
      // Ignore cross-origin stylesheets and other access errors
      // This prevents CORS errors when accessing external stylesheets
    }
  });

  const printControlsCSS = `
.print-checkbox, .print-radio { display: flex; align-items: center; gap: 8px; }
.print-checkbox .box { width: 16px; height: 16px; border: 2px solid #000; border-radius: 2px; position: relative; display: inline-block; }
.print-checkbox.checked .box { background: #000; }
.print-checkbox.checked .box::after { content: '✓'; position: absolute; top: -4px; left: 2px; font-size: 14px; color: #fff; }
.print-radio .dot { width: 16px; height: 16px; border: 2px solid #000; border-radius: 50%; position: relative; display: inline-block; }
.print-radio.checked .dot::after { content: ''; position: absolute; width: 8px; height: 8px; border-radius: 50%; background: #000; top: 50%; left: 50%; transform: translate(-50%, -50%); }
.print-switch { display: flex; align-items: center; gap: 8px; }
.print-switch .track { width: 28px; height: 16px; border-radius: 16px; border: 2px solid #000; position: relative; display: inline-block; }
.print-switch .thumb { width: 10px; height: 10px; border-radius: 50%; background: #000; position: absolute; top: 50%; left: 3px; transform: translateY(-50%); }
.print-switch.checked .thumb { left: 13px; }
.print-textfield { display: flex; flex-direction: column; gap: 6px; margin: 8px 0; }
.print-textfield .label { font-size: 12px; color: #666; }
.print-textfield .value { border-bottom: 2px solid #000; padding: 6px 2px; min-height: 20px; white-space: pre-wrap; word-break: break-word; }
#start-assessment-button { display: none !important; }
#topbar-actions { display: none !important; }
#footer { display: none !important; }
#landing-page-description { display: none !important; }
`;

  const printLayoutCSS = `
@page { size: A4; margin: 0mm; }
@media print {
  html, body { margin: 0; padding: 0; background: #fff; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  section.survey-section { break-inside: avoid; page-break-inside: avoid; margin: 0 0 12mm 0; }
  /* Break helper used by html2pdf to force a new page */
  .html2pdf__page-break { display: block; page-break-before: always; break-before: page; height: 0; }
  #solution-print-section { break-inside: avoid !important; page-break-inside: avoid !important; page-break-before: always; page-break-after: always; }
  #business-case-print-section { break-inside: avoid !important; page-break-inside: avoid !important; page-break-before: always; page-break-after: always; }
  #landing-page-root { break-inside: avoid !important; page-break-inside: avoid !important; page-break-before: always; page-break-after: always; margin: 0; }
  table, tr, td, th { break-inside: avoid; page-break-inside: avoid; }
  img { max-width: 100%; height: auto; }
}
`;

  return `\n${linkTags}\n<style>${cssText}</style>\n${styleTags}\n<style>body { margin: 0; background: #fff; }\n${printControlsCSS}\n${printLayoutCSS}</style>`;
}

export function extractPrintableSection(element) {
  if (!element) return '';
  const clone = element.cloneNode(true);

  const srcFormEls = element.querySelectorAll('input, textarea, select');
  const dstFormEls = clone.querySelectorAll('input, textarea, select');
  srcFormEls.forEach((srcEl, idx) => {
    const dstEl = dstFormEls[idx];
    if (!dstEl) return;
    const tag = srcEl.tagName;
    if (tag === 'INPUT') {
      const type = srcEl.getAttribute('type') || '';
      if (type === 'checkbox' || type === 'radio') {
        // eslint-disable-next-line no-unused-expressions
        srcEl.checked
          ? dstEl.setAttribute('checked', '')
          : dstEl.removeAttribute('checked');
      } else {
        dstEl.setAttribute('value', srcEl.value || '');
      }
    } else if (tag === 'TEXTAREA') {
      dstEl.textContent = srcEl.value || '';
    } else if (tag === 'SELECT') {
      const srcOptions = srcEl.options || [];
      const dstOptions = dstEl.options || [];
      Array.from(srcOptions).forEach((opt, i) => {
        if (dstOptions[i]) dstOptions[i].selected = opt.selected;
      });
    }
  });

  const srcTextFields = element.querySelectorAll('mdui-text-field');
  const dstTextFields = clone.querySelectorAll('mdui-text-field');
  const escapeHtml = (str = '') =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  srcTextFields.forEach((srcNode, idx) => {
    const dstNode = dstTextFields[idx];
    if (!dstNode) return;
    const val = srcNode.value ?? srcNode.getAttribute('value') ?? '';
    const label = srcNode.getAttribute('label') || '';
    const html = `\n<div class="print-textfield">\n  ${
      label ? `<div class="label">${escapeHtml(label)}</div>` : ''
    }\n  <div class="value">${escapeHtml(val)}</div>\n</div>\n`;
    const container = document.createElement('div');
    container.innerHTML = html;
    const replacement = container.firstElementChild;
    if (replacement) dstNode.replaceWith(replacement);
  });

  const replaceMDUIForPrint = root => {
    const replaceNode = (node, html) => {
      const container = document.createElement('div');
      container.innerHTML = html;
      const replacement = container.firstElementChild;
      if (replacement) node.replaceWith(replacement);
    };
    const isNodeChecked = node => {
      try {
        return (
          node.hasAttribute('checked') ||
          node.getAttribute('checked') !== null ||
          node.checked === true ||
          node.getAttribute('aria-checked') === 'true'
        );
      } catch {
        return false;
      }
    };
    root.querySelectorAll('mdui-radio-group').forEach(group => {
      const radios = group.querySelectorAll('mdui-radio');
      let radioHtml = '';

      radios.forEach(node => {
        const isChecked = isNodeChecked(node);
        const label = node.textContent ? node.textContent.trim() : '';

        radioHtml += `\n<div class="print-radio${
          isChecked ? ' checked' : ''
        }" style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">\n  <span class="dot" style="width: 16px; height: 16px; border: 2px solid #000; border-radius: 50%; position: relative; display: inline-block; background: #fff; min-width: 16px; min-height: 16px;">${
          isChecked
            ? '<span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 8px; height: 8px; background: #000; border-radius: 50%; display: block;"></span>'
            : ''
        }</span>\n  <span class="label" style="margin-left: 4px;">${label}</span>\n</div>`;
      });

      const groupHtml = `\n<div class="print-radio-group" style="margin: 8px 0;">${radioHtml}\n</div>\n`;
      replaceNode(group, groupHtml);
    });

    root.querySelectorAll('mdui-checkbox').forEach(node => {
      const isChecked = isNodeChecked(node);
      const label = node.textContent ? node.textContent.trim() : '';
      const html = `\n<div class="print-checkbox${
        isChecked ? ' checked' : ''
      }">\n  <span class="box"></span>\n  <span class="label">${label}</span>\n</div>\n`;
      replaceNode(node, html);
    });
    root.querySelectorAll('mdui-switch').forEach(node => {
      const isChecked = isNodeChecked(node);
      const label = node.textContent ? node.textContent.trim() : '';
      const html = `\n<div class="print-switch${
        isChecked ? ' checked' : ''
      }">\n  <span class="track"><span class="thumb"></span></span>\n  <span class="label">${label}</span>\n</div>\n`;
      replaceNode(node, html);
    });
  };
  replaceMDUIForPrint(clone);

  return `<section class="survey-section">${clone.outerHTML}</section>`;
}

export function buildFullPrintHtml(sections, headHtml) {
  const head = headHtml || buildPrintHead();
  const parts = sections.filter(Boolean);
  const body =
    parts.length > 1
      ? [
          parts[0],
          '<div class="html2pdf__page-break"></div>',
          ...parts.slice(1)
        ].join('\n')
      : parts.join('\n');
  return `<!doctype html>\n<html>\n<head>\n<meta charset="utf-8"/>\n${head}\n</head>\n<body style="padding:8px">${body}</body>\n</html>`;
}
